import React, { useState, useEffect, useMemo } from "react";
import { Select, Descriptions, Space, Skeleton } from "antd";
import { TagOutlined, ShopOutlined, BarcodeOutlined } from "@ant-design/icons";
import axiosInstance from "utilities/axios";
import useRoleCheck from "utilities/hooks/roleCheck";

const mapFunc = (e) => {
  return {
    value: e,
    label: e,
  };
};
function EquipmentSelector({ value, onChange, orderable }) {
  //This compontent uses three dropdowns to return the integer for the selected equipment type
  const { hasPermission } = useRoleCheck();
  const [equipmentOptions, setEquipmentTypes] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const categoryOptions = Array.from(
    new Set(equipmentOptions.map((e) => e.category))
  ).map(mapFunc);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const brandOptions = Array.from(
    new Set(
      equipmentOptions
        .filter((e) => e.category === selectedCategory)
        .map((e) => e.brand)
    )
  ).map(mapFunc);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const modelOptions = useMemo(
    () =>
      equipmentOptions
        .filter(
          (e) => e.brand === selectedBrand && e.category === selectedCategory
        )
        .map((e) => e.model)
        .map(mapFunc),
    [equipmentOptions, selectedBrand, selectedCategory]
  );

  const [selectedModel, setSelectedModel] = useState(null);

  useEffect(() => {
    function fetchEquipmentTypes() {
      var params = orderable ? "?orderableItem=true" : "";
      axiosInstance.get("/equipment" + params).then((result) => {
        const nonAudioEquipmentCategories = ["Digital Signature Pad", "Barcode Scanner"];

        if (hasPermission(["Audiology.Admin", "Audiology.Write"])) {
          setEquipmentTypes(result.data.filter((e) => !nonAudioEquipmentCategories.includes(e.category)))
          return;
        }

        setEquipmentTypes(result.data);
      });
    }
    fetchEquipmentTypes();
  }, [orderable]);

  useEffect(() => {
    if (onChange) {
      onChange(selectedEquipment?.id);
    }
  }, [selectedEquipment, onChange]);

  if (!equipmentOptions.length) {
    return <Skeleton active />;
  }
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <label>
          <Space>
            <TagOutlined /> Category
          </Space>
        </label>
        <Select
          value={selectedCategory}
          style={{ width: "100%" }}
          autoFocus
          onChange={(value) => {
            setSelectedCategory(value);
            setSelectedBrand(null);
            setSelectedModel(null);
            setSelectedEquipment(null);
          }}
          placeholder="Select a category"
          defaultActiveFirstOption={false}
          options={categoryOptions}
        />
      </div>
      {selectedCategory && (
        <div style={{ marginBottom: "16px" }}>
          <label>
            <Space>
              <ShopOutlined /> Brand
            </Space>
          </label>
          <Select
            value={selectedBrand}
            style={{ width: "100%" }}
            onChange={(value) => {
              setSelectedBrand(value);
              setSelectedModel(null);
              setSelectedEquipment(null);
            }}
            placeholder="Select a brand"
            defaultActiveFirstOption={false}
            options={brandOptions}
          />
        </div>
      )}
      {selectedBrand && (
        <div style={{ marginBottom: "16px" }}>
          <label>
            <Space>
              <BarcodeOutlined /> Model
            </Space>
          </label>
          <Select
            value={selectedModel}
            style={{ width: "100%" }}
            onChange={(value) => {
              setSelectedModel(value);
              setSelectedEquipment(
                equipmentOptions.find(
                  (e) =>
                    e.brand === selectedBrand &&
                    e.category === selectedCategory &&
                    e.model === value
                )
              );
            }}
            placeholder="Select a model"
            defaultActiveFirstOption={false}
            showSearch
            options={modelOptions}
          />
        </div>
      )}
      {selectedEquipment && orderable && (
        <Descriptions bordered style={{ marginBottom: "20px" }}>
          <Descriptions.Item label="Part Number">
            {selectedEquipment.partNumber}
          </Descriptions.Item>
          <Descriptions.Item label="Price">
            {selectedEquipment.price}
          </Descriptions.Item>
        </Descriptions>
      )}
    </>
  );
}

const EquipmentSelectorOrderable = ({ value, onChange }) => {
  return <EquipmentSelector value={value} onChange={onChange} orderable />;
};

export { EquipmentSelectorOrderable, EquipmentSelector };
